/** Supplier & product performance tab - waste linked to supplier quality (damaged deliveries,
 * short shelf-life, rejected goods, supplier-related spoilage), products with recurring waste,
 * pack sizes and minimum order quantities contributing to overstock, and a supplier comparison
 * by waste impact. */

import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis } from 'recharts';
import { useTranslation } from 'react-i18next';
import { Section } from '../../../components/ui/Section';
import { applyRoundAndFormat } from '../../../utils';
import { SUPPLIER_WASTE } from '../data';
import { StatusChip } from './StatusChip';

interface SupplierPerformanceTabProps {
  currencyCode?: string;
}

const SupplierPerformanceTab = ({ currencyCode }: SupplierPerformanceTabProps) => {
  const { t } = useTranslation('common');
  const ranked = [...SUPPLIER_WASTE].sort((a, b) => b.wasteCost - a.wasteCost);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <Section>
        <Box sx={{ p: 4 }}>
          <Typography variant="textMD"
            fontWeight={600}
            sx={{ mb: 3 }}>
            {t('wasteDashboardSupplierComparisonTitle', 'Supplier comparison by waste impact')}
          </Typography>
          <Box sx={{ width: '100%', height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={ranked}
                layout="vertical"
                margin={{ left: 60 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis type="category"
                  dataKey="supplier"
                  width={150} />
                <RechartsTooltip formatter={(value: number) => applyRoundAndFormat(value, currencyCode)} />
                <Bar dataKey="wasteCost"
                  name={t('wasteDashboardWasteCost', 'Waste cost')}
                  fill="#F04438" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      </Section>

      <Section>
        <Box sx={{ p: 4 }}>
          <Typography variant="textMD"
            fontWeight={600}
            sx={{ mb: 3 }}>
            {t('wasteDashboardSupplierQualityTitle', 'Waste linked to supplier quality')}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('wasteDashboardSupplier', 'Supplier')}</TableCell>
                <TableCell align="right">{t('wasteDashboardWasteCost', 'Waste cost')}</TableCell>
                <TableCell align="right">{t('wasteDashboardDamagedDeliveries', 'Damaged deliveries')}</TableCell>
                <TableCell align="right">{t('wasteDashboardShortShelfLife', 'Short shelf-life deliveries')}</TableCell>
                <TableCell align="right">{t('wasteDashboardRejectedGoods', 'Rejected goods')}</TableCell>
                <TableCell align="right">{t('wasteDashboardRecurringWasteProducts', 'Recurring waste products')}</TableCell>
                <TableCell>{t('wasteDashboardContributingFactors', 'Contributing factors')}</TableCell>
                <TableCell align="right">{t('wasteDashboardQualityScore', 'Quality score')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ranked.map((row) => (
                <TableRow key={row.supplier}>
                  <TableCell>{row.supplier}</TableCell>
                  <TableCell align="right">{applyRoundAndFormat(row.wasteCost, currencyCode)}</TableCell>
                  <TableCell align="right">{row.damagedDeliveries}</TableCell>
                  <TableCell align="right">{row.shortShelfLifeDeliveries}</TableCell>
                  <TableCell align="right">{row.rejectedGoods}</TableCell>
                  <TableCell align="right">{row.recurringWasteProducts}</TableCell>
                  <TableCell>
                    {row.packSizeIssue && t('wasteDashboardPackSizeTooLarge', 'Pack size too large')}
                    {row.packSizeIssue && row.minimumOrderQuantityIssue && ', '}
                    {row.minimumOrderQuantityIssue && t('wasteDashboardMoqDriveOverstock', 'MOQ drives overstock')}
                    {!row.packSizeIssue && !row.minimumOrderQuantityIssue && '\u2013'}
                  </TableCell>
                  <TableCell align="right">
                    <StatusChip status={row.qualityScore >= 85 ? 'good' : row.qualityScore >= 70 ? 'warning' : 'critical'}
                      label={`${row.qualityScore}/100`} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Section>
    </Box>
  );
};

export default SupplierPerformanceTab;
